import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTachometerAlt, 
  faUsers, 
  faChartBar,
  faSignOutAlt,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

function AffiliateSidebar({ isOpen, toggleSidebar }) {
  const { currentUser, logout } = useContext(AuthContext);
  const { theme } = useTheme();
  const location = useLocation();

  // Check if user is affiliate admin (can manage team and see all reports)
  const isAffiliateAdmin = currentUser?.isAffiliateAdmin === 'True' || currentUser?.isAffiliateAdmin === true;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    {
      path: '/affiliate-dashboard',
      icon: faTachometerAlt,
      label: 'Dashboard',
      available: true
    },
    {
      path: '/affiliate-reports',
      icon: faChartBar,
      label: 'Reports',
      available: true
    },
    {
      path: '/affiliate-team',
      icon: faUsers,
      label: 'Manage Team',
      available: isAffiliateAdmin
    }
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`fixed left-0 top-0 h-full shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:shadow-none w-64`}
        style={{ backgroundColor: theme.sidebarBG || '#ffffff' }}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <img 
              src={theme.loginImage} 
              alt="Logo" 
              className="h-8 w-auto mr-3" 
            />
            <div>
              <h2 
                className="text-lg font-semibold"
                style={{ color: theme.textPrimary || '#1f2937' }}
              >
                Partner Portal
              </h2>
              <p 
                className="text-xs"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                {isAffiliateAdmin ? 'Admin Access' : 'Member Access'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100"
          >
            <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
          </button>
        </div>

        {/* User Info */}
        <div 
          className="p-4 border-b border-gray-200"
          style={{ backgroundColor: theme.sidebarUserBG || '#f9fafb' }}
        >
          <div className="flex items-center">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
              style={{ backgroundColor: theme.primaryColor || '#3b82f6' }}
            >
              {currentUser?.displayName ? 
                `${currentUser.displayName.split(' ')[0]?.[0]}${currentUser.displayName.split(' ')[1]?.[0] || ''}` : 
                currentUser?.email?.[0]?.toUpperCase() || 'U'
              }
            </div>
            <div className="ml-3">
              <p 
                className="text-sm font-medium"
                style={{ color: theme.textPrimary || '#111827' }}
              >
                {currentUser?.displayName || currentUser?.email}
              </p>
              <p 
                className="text-xs"
                style={{ color: theme.textSecondary || '#6b7280' }}
              >
                {isAffiliateAdmin ? 'Affiliate Admin' : 'Team Member'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              if (!item.available) return null;
              
              const isActive = location.pathname === item.path;
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => {
                      // Close mobile sidebar when navigating
                      if (window.innerWidth < 1024) {
                        toggleSidebar();
                      }
                    }}
                    className="flex items-center px-4 py-3 rounded-lg transition-colors duration-200"
                    style={
                      isActive
                        ? {
                            backgroundColor: theme.activeMenuBG || '#dbeafe', 
                            color: theme.activeMenuText || theme.primaryColor || '#1d4ed8',
                            borderRight: `4px solid ${theme.primaryColor || '#3b82f6'}`
                          }
                        : {
                            color: theme.textPrimary || '#374151'
                          }
                    }
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        const linkElement = e.currentTarget;
                        linkElement.style.backgroundColor = theme.hoverMenuBG || '#f3f4f6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        const linkElement = e.currentTarget;
                        linkElement.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={item.icon} 
                      className="mr-3 text-lg"
                      style={{ 
                        color: isActive 
                          ? theme.primaryColor || '#3b82f6'
                          : theme.textSecondary || '#6b7280' 
                      }}
                    />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer / Logout */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3 rounded-lg transition-colors duration-200"
            style={{ color: theme.textPrimary || '#374151' }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#fef2f2';
              e.target.style.color = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = theme.textPrimary || '#374151';
            }}
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 text-lg" />
            <span className="font-medium">Sign Out</span>
          </button>
          
          {/* Version info */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p 
              className="text-xs text-center"
              style={{ color: theme.textSecondary || '#6b7280' }}
            >
              Partner Portal v1.0
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default AffiliateSidebar;